import { Controller } from "@hotwired/stimulus"

// The blog article editor. A markdown toolbar over the body field, a Write / Preview switch (the preview is the real article styling,
// in an iframe so it cannot be disturbed by the admin's own styles), image upload, and a checklist and word count that update a moment
// after you stop typing. The server does the rendering and the checks (the same code the public page uses), so what you see is what ships.
export default class extends Controller {
  static targets = ["title", "slug", "body", "tabWrite", "tabPreview", "writePane", "previewPane", "frame", "stats", "notice", "file", "checklist", "score", "bar"]
  static values = { previewUrl: String, uploadUrl: String, new: Boolean }

  connect() {
    this.slugTouched = !this.newValue || this.slugTarget.value.trim() !== ""
    this.dirty = false
    this.requestId = 0
    this.html = ""
    this.element.querySelectorAll("[data-blog-editor-count]").forEach((field) => {
      field.addEventListener("input", () => this.count(field))
      this.count(field)
    })
    this.guard = (event) => { if (this.dirty) { event.preventDefault(); event.returnValue = "" } }
    window.addEventListener("beforeunload", this.guard)
    this.element.addEventListener("submit", () => { this.dirty = false })
    this.refresh()
  }

  disconnect() {
    clearTimeout(this.timer)
    window.removeEventListener("beforeunload", this.guard)
  }

  // ---- title, slug, counters -------------------------------------------------------------------------------------

  titleChanged() {
    if (!this.slugTouched) this.slugTarget.value = this.parameterize(this.titleTarget.value)
  }

  slugEdited() {
    this.slugTouched = true
  }

  parameterize(text) {
    return text.toLowerCase().normalize("NFKD").replace(/[̀-ͯ]/g, "").replace(/[^\w\s-]/g, "").trim().replace(/[\s_-]+/g, "-")
  }

  count(field) {
    const output = document.getElementById(field.dataset.blogEditorCount)
    if (output) output.textContent = `${field.value.length} characters.`
  }

  // ---- markdown toolbar ------------------------------------------------------------------------------------------

  format({ params: { before, after, placeholder, block } }) {
    const field = this.bodyTarget
    const { selectionStart: start, selectionEnd: end, value } = field
    const selected = value.slice(start, end) || placeholder
    let text
    if (block) {
      text = selected.split("\n").map((line) => `${before}${line.replace(/^(#{1,6} |[-*] |\d+\. |> )/, "")}`).join("\n")
      text = this.onOwnLine(value, start, text)
    } else {
      text = `${before}${selected}${after}`
    }
    this.replace(start, end, text, block ? null : [ before.length, before.length + selected.length ])
  }

  table() {
    const { selectionStart: start, selectionEnd: end, value } = this.bodyTarget
    const text = this.onOwnLine(value, start, "| Column | Column | Column |\n|---|---|---|\n| Cell | Cell | Cell |\n| Cell | Cell | Cell |")
    this.replace(start, end, text, null)
  }

  cta() {
    const { selectionStart: start, selectionEnd: end, value } = this.bodyTarget
    this.replace(start, end, this.onOwnLine(value, start, "{{cta}}"), null)
  }

  // Block elements need a blank line before them (and after), or markdown folds them into the paragraph above.
  onOwnLine(value, start, text) {
    const before = value.slice(0, start)
    const lead = before === "" || before.endsWith("\n\n") ? "" : before.endsWith("\n") ? "\n" : "\n\n"
    return `${lead}${text}\n\n`
  }

  replace(start, end, text, selectRange) {
    const field = this.bodyTarget
    field.focus()
    field.setRangeText(text, start, end, "end")
    if (selectRange) field.setSelectionRange(start + selectRange[0], start + selectRange[1])
    field.dispatchEvent(new Event("input", { bubbles: true }))
  }

  // ---- images ----------------------------------------------------------------------------------------------------

  chooseImage() {
    this.fileTarget.click()
  }

  async upload() {
    const file = this.fileTarget.files[0]
    if (!file) return

    this.noticeTarget.textContent = "Uploading..."
    const body = new FormData()
    body.append("file", file)
    try {
      const response = await fetch(this.uploadUrlValue, { method: "POST", body, headers: { "X-CSRF-Token": this.csrf, Accept: "application/json" } })
      const data = await response.json()
      if (!response.ok) throw new Error(data.error || "The upload failed.")

      const { selectionStart: start, selectionEnd: end, value } = this.bodyTarget
      this.replace(start, end, this.onOwnLine(value, start, `![${data.name}](${data.url})`), null)
      this.noticeTarget.textContent = ""
    } catch (error) {
      this.noticeTarget.textContent = error.message
    } finally {
      this.fileTarget.value = ""
    }
  }

  get csrf() {
    return document.querySelector("meta[name=csrf-token]")?.content || ""
  }

  // ---- write / preview -------------------------------------------------------------------------------------------

  async showPreview() {
    this.setTab(true)
    await this.refresh()
    this.renderFrame()
  }

  showWrite() {
    this.setTab(false)
  }

  setTab(preview) {
    this.writePaneTarget.hidden = preview
    this.previewPaneTarget.hidden = !preview
    this.previewing = preview
    ;[[this.tabWriteTarget, !preview], [this.tabPreviewTarget, preview]].forEach(([button, active]) => {
      button.classList.toggle("btn-primary", active)
      button.classList.toggle("btn-outline-secondary", !active)
      button.setAttribute("aria-selected", String(active))
    })
  }

  // ---- live checks -----------------------------------------------------------------------------------------------

  schedule() {
    this.dirty = true
    clearTimeout(this.timer)
    this.timer = setTimeout(() => this.refresh().then(() => { if (this.previewing) this.renderFrame() }), 700)
  }

  async refresh() {
    const id = ++this.requestId
    const data = new FormData(this.element)
    ;[ "_method", "blog_post[cover_image]" ].forEach((key) => data.delete(key))
    try {
      const response = await fetch(this.previewUrlValue, { method: "POST", body: data, headers: { "X-CSRF-Token": this.csrf, Accept: "application/json" } })
      if (!response.ok || id !== this.requestId) return

      this.apply(await response.json())
    } catch (_error) { /* offline or signed out: the form still saves normally */ }
  }

  apply(result) {
    this.html = result.html
    const stat = (name, value) => { const node = this.statsTarget.querySelector(`[data-stat=${name}]`); if (node) node.textContent = value }
    stat("words", result.words.toLocaleString("en-IN"))
    stat("minutes", result.reading_minutes)
    stat("headings", result.headings)
    stat("links", result.internal_links)

    const done = result.checks.filter((check) => check.passed).length
    this.scoreTarget.textContent = `${done} of ${result.checks.length}`
    this.barTarget.style.width = `${Math.round((done / result.checks.length) * 100)}%`
    this.checklistTarget.replaceChildren(...result.checks.map((check) => {
      const item = document.createElement("li")
      item.className = check.passed ? "is-done" : "is-todo"
      const icon = document.createElement("i")
      icon.className = `bi ${check.passed ? "bi-check-circle-fill" : "bi-circle"}`
      icon.setAttribute("aria-hidden", "true")
      const label = document.createElement("span")
      label.textContent = check.label
      if (!check.passed) {
        const hint = document.createElement("small")
        hint.textContent = check.hint
        label.appendChild(hint)
      }
      const state = document.createElement("span")
      state.className = "visually-hidden"
      state.textContent = check.passed ? "done" : "to do"
      item.append(icon, label, state)
      return item
    }))
  }

  renderFrame() {
    this.frameTarget.srcdoc = `<!doctype html><html lang="en"><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1">
      <link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Inter:opsz,wght@14..32,400..700&display=swap">
      <link rel="stylesheet" href="/marketing.css"><link rel="stylesheet" href="/blog.css">
      <style>body{margin:0;padding:1.5rem;background:#fff;font-family:Inter,ui-sans-serif,system-ui,-apple-system,Segoe UI,sans-serif;color:#1d1d1d}.bl-cta-icon i{display:none}</style></head>
      <body class="bl-shell" style="padding-top:1.5rem"><div class="bl-article" style="max-width:46rem;margin:0 auto"><div class="bl-prose">${this.html || "<p>Nothing to preview yet.</p>"}</div></div></body></html>`
  }
}
